import { useState, useMemo, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import useGetPublicSessionQuery from 'features/publicRoleplay/services/queries/useGetPublicSession';
import useUpdatePublicScenarioMutation from 'features/publicRoleplay/services/mutations/useUpdatePublicScenario';
import PublicRealPlayHelper from 'features/publicRoleplay/publicRealPlay.helper';

const SubmitPublicRoleplayLogic = () => {
  const history = useHistory();
  const { token } = useParams();

  const [enabled, setEnabled] = useState(false);

  const {
    isLoadingVideoTemp,
    scenario_uuid,
    tempVideoUrl,
    session_uuid,
    status,
    assignment_uuid,
    scenarios,
    selectedScenario,
  } = PublicRealPlayHelper();

  const allHaveVideos = useMemo(() => {
    if (!scenarios || scenarios?.length === 0) return false;
    return scenarios?.every((s) => {
      const response = s?.response_retake || s?.response;
      const video = response?.video;
      return video !== null && video !== '';
    });
  }, [scenarios]);

  const { refetch } = useGetPublicSessionQuery({
    assignment_uuid,
    scenario_uuid,
    session_uuid,
    enabled: enabled && !!session_uuid,
  });

  const { mutate: publicScenarioUpdate, isLoading: isLoadingScenario } =
    useUpdatePublicScenarioMutation();

  const isLastScenario = useMemo(() => {
    if (scenarios?.length === 1) return true;
    const allHaveVideos = scenarios
      ?.filter((s) => s?.uuid !== scenario_uuid)
      ?.map((s) => s?.response_retake || s?.response)
      ?.every((response) => !!response?.video);
    return allHaveVideos;
  }, [scenarios, scenario_uuid]);

  const label = useMemo(() => {
    if (isLoadingScenario) return 'Loading...';
    if (isLoadingVideoTemp) return 'Loading video...';
    if (isLastScenario) return 'Save and submit';
    return 'Save and continue';
  }, [isLoadingScenario, isLastScenario, isLoadingVideoTemp]);

  const navigateNext = () => {
    if (isLastScenario) {
      history.push(`/public-roleplay/${token}/submit`);
    }
  };

  const onSubmit = () => {
    if (enabled) {
      refetch();
      return;
    }
    setEnabled(true);
  };

  const handleAfterCheckSession = () => {
    setEnabled(false);
    if (!tempVideoUrl || !scenario_uuid) return;

    const response_uuid = selectedScenario?.response?.uuid;

    publicScenarioUpdate(
      { token, url_video: tempVideoUrl, response_uuid, session_uuid },
      { onSuccess }
    );
  };

  const handleSubmit = () => history.push(`/public-roleplay/${token}/submit`);

  const onSuccess = () => {
    navigateNext();
  };

  const isDisabled = useMemo(
    () => isLoadingScenario || tempVideoUrl === '',
    [isLoadingScenario, tempVideoUrl]
  );

  useEffect(() => {
    if (!enabled) return;
    if (status === 'DRAFT') handleAfterCheckSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, status]);

  return {
    isLoadingVideoTemp,
    allHaveVideos,
    label,
    onSubmit,
    handleSubmit,
    isDisabled,
    status,
  };
};

export default SubmitPublicRoleplayLogic;
